import { z } from 'zod';
import type { MonitorStatusCode } from './monitoring';

// ------------------------------------------------------------------ customers (/customers)

export const CPE_CONNECTION_STATUSES = ['connected', 'disconnected', 'suspended', 'maintenance'] as const;
export const CPE_CONNECTION_LABELS: Record<(typeof CPE_CONNECTION_STATUSES)[number], string> = {
  connected: 'Terhubung',
  disconnected: 'Terputus',
  suspended: 'Diisolir',
  maintenance: 'Maintenance',
};

export const customerListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
  q: z.string().trim().max(100).optional(),
  area: z.string().max(40).optional(),
  connection: z.enum(CPE_CONNECTION_STATUSES).optional(),
  /** `1` = only customers not linked to an ODP port yet. */
  unlinked: z.enum(['0', '1']).optional(),
  odp: z.string().uuid().optional(),
});
export type CustomerListQuery = z.infer<typeof customerListQuerySchema>;

export interface CustomerListItem {
  id: string;
  code: string;
  name: string;
  status: string;
  areaCode: string | null;
  customerName: string | null;
  customerRef: string | null;
  phone: string | null;
  address: string | null;
  serialNumber: string | null;
  cpeKind: string | null;
  servicePlan: string | null;
  connection: string | null;
  odp: { id: string; code: string; name: string; port: string } | null;
  mon: MonitorStatusCode | null;
}

export interface CustomerSummary {
  total: number;
  byConnection: Record<string, number>;
  unlinked: number;
}

// ------------------------------------------------------------------ incidents (/alerts)

export const INCIDENT_STATUSES = ['open', 'acknowledged', 'resolved'] as const;
export type IncidentStatus = (typeof INCIDENT_STATUSES)[number];
export const INCIDENT_STATUS_LABELS: Record<IncidentStatus, string> = {
  open: 'Aktif',
  acknowledged: 'Diakui',
  resolved: 'Selesai',
};

export const INCIDENT_KIND_LABELS: Record<string, string> = { device_down: 'Perangkat offline' };

export const incidentListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
  q: z.string().trim().max(100).optional(),
  area: z.string().max(40).optional(),
  status: z.enum(INCIDENT_STATUSES).optional(),
});
export type IncidentListQuery = z.infer<typeof incidentListQuerySchema>;

export const incidentAckSchema = z.object({ note: z.string().trim().max(500).default('') });

export interface Incident {
  id: string;
  kind: string;
  severity: 'critical' | 'warning';
  status: IncidentStatus;
  assetId: string;
  assetCode: string;
  assetName: string;
  typeCode: string;
  areaCode: string | null;
  /** Current monitoring status of the device. */
  deviceStatus: MonitorStatusCode | null;
  startedAt: string;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  ackNote: string | null;
  resolvedAt: string | null;
  /** null = closed automatically when the device came back. */
  resolvedBy: string | null;
}

export interface IncidentSummary {
  open: number;
  acknowledged: number;
  resolvedToday: number;
}

/** Published by the worker on MONITORING_EVENTS_CHANNEL next to status events. */
export interface IncidentEvent {
  kind: 'incident';
  incidentId: string;
  assetId: string;
  areaCode: string | null;
  status: IncidentStatus;
}
